import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  description?: ReactNode;
  /** Renders the checkbox inside a bordered option card. */
  card?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, card = false, className, id, checked, disabled, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const descId = description ? `${inputId}-desc` : undefined;
  return (
    <label
      htmlFor={inputId}
      className={cn(
        'flex cursor-pointer items-start gap-3',
        card && 'rounded-lg border p-4 transition-colors',
        card && (checked ? 'border-primary bg-primary-soft/60' : 'border-line hover:border-line-strong hover:bg-surface-muted'),
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-describedby={descId}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-line-strong accent-[rgb(var(--color-primary))] focus-visible:shadow-focus disabled:cursor-not-allowed"
        {...rest}
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink">{label}</span>
        {description && (
          <span id={descId} className="mt-0.5 block text-[13px] text-ink-secondary">
            {description}
          </span>
        )}
      </span>
    </label>
  );
});
